import { apiRequest } from "./api-request";
import { ModelConfigError } from "./model-config";

// Settings · KI · Recherchequellen → BFF (/ai/research-sources) → gemstone, next to the provider keys.
// Same transport and error contract as the model config; a blank `apiKey` leaves the stored key unchanged.

export type ResearchSource = "pubmed";

// What one question may search (`forwardedProps.searchSources`): the web, or a research source the
// admin offers. Narrows what is offered, never widens it.
export type SearchSource = "web" | ResearchSource;

// What the globe can open right now (GET /ai/search-sources, decided by the agent-service: deployment on,
// admin switch on, gateway connected). Only for showing it; the agent-service narrows every turn itself.
export type OfferedSearchSources = Record<SearchSource, boolean>;

export async function loadOfferedSearchSources(): Promise<OfferedSearchSources> {
  const { response } = await apiRequest("ai/search-sources", { method: "GET" });
  if (!response.ok) throw new ModelConfigError(response.status, `Failed to load search sources (${response.status})`);
  const body = (await response.json()) as Partial<OfferedSearchSources>;
  return { web: body.web === true, pubmed: body.pubmed === true };
}

export interface ResearchSourceSetting {
  enabled: boolean;
  apiKey: string;
}

// Only sources the admin has ever set; absent = off, no key.
export interface ResearchSourcesState {
  sources: Partial<Record<ResearchSource, ResearchSourceSetting>>;
}

export async function loadResearchSources(): Promise<ResearchSourcesState> {
  const { response } = await apiRequest("ai/research-sources", { method: "GET" });
  if (!response.ok)
    throw new ModelConfigError(response.status, `Failed to load research sources (${response.status})`);
  return (await response.json()) as ResearchSourcesState;
}

export async function saveResearchSources(input: ResearchSourcesState): Promise<void> {
  const { response } = await apiRequest("ai/research-sources", {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    throw new ModelConfigError(response.status, `Failed to save research sources (${response.status})`);
}
